'use client';

/**
 * BigStatsMotion — the light big-stats band's ONE motion engine (R4-P3
 * masterplan §2.3, [AMENDED per A6 + C5]: "New number/curve motion lives
 * ONLY in a new `BigStatsMotion.tsx`, mounts only when `snapshot !== null`,
 * runs once, owns complete cleanup").
 *
 * Renders nothing (`return null`) — a pure `useEffect` engine, same shape as
 * the deleted `SocialProofMotionController`: queries pre-rendered DOM nodes
 * inside `sectionRef` via stable `data-*` hooks and drives ONE compositor-
 * safe (transform/opacity only, `ui-quality.md`) entrance reveal, once, on
 * in-view.
 *
 * Scope note (composition, not a fork): `BigStat`'s own number ticker is
 * SELF-CONTAINED — it starts its `NumberTicker` on mount with its own
 * SSR-safe PRM guard (same shape as `ui/StatTile.tsx`, per `BigStat.tsx`'s
 * doc comment) and is a FROZEN P3-0 export (masterplan [AMENDED per D4]) —
 * this engine does not (and must not) reach into it. What "number/curve
 * motion" means HERE is the shared block-level reveal that brings the
 * 3-stat row and the `BigStatCurve` sweep in together as the band scrolls
 * into view; `BigStat`'s internal tick-up runs independently, exactly as
 * shipped. A literal per-path `stroke-dashoffset` "draw" (the pre-amendment
 * §2.3 prose) was considered and REJECTED here: `BigStatCurve` is a frozen
 * `ui-light/**` primitive with no exposed animation hook, and driving one
 * via `SVGGeometryElement.getTotalLength()` is unavailable in this repo's
 * jsdom test environment (no polyfill on disk) — forking the primitive to
 * add one would violate Law #12's anti-fork directive for a decorative
 * accent that is already fully filled/static (Law #1 filled-mock) and
 * already carries its own `IllustrativeLabel` (Law #8). FLAGGED to the plan
 * owner; the compositor-safe block reveal below satisfies the "new file,
 * gated, once, full cleanup" contract without either risk.
 *
 * Engine: vanilla `import { inView } from 'motion'` + raw WAAPI
 * `element.animate()` (transform/opacity only) — no `motion/react`, no
 * `framer-motion` (Law #11). `BIG_STATS_TIMING` below is this scene's ONE
 * frozen timing table, sourcing `landing-motion.ts` tokens exclusively —
 * `check-landing-choreography.mjs` auto-discovers any `*_TIMING`-suffixed
 * const ([AMENDED per A4]), no separate registration needed.
 *
 * [MINOR finding, r4-p3-review-correctness.md — investigated, NOT applied
 * here]: the reviewer flagged that the initial "hide until reveal" write
 * happening in `useEffect` (after paint) rather than `useLayoutEffect`
 * (before paint) risks a one-frame flash for an already-in-viewport-at-mount
 * visitor, and suggested splitting the hide write into its own
 * `useLayoutEffect`. That split was implemented and then REVERTED after it
 * broke this file's own passing tests: `sectionRef` here is a ref OWNED by
 * the CALLER (`SocialProofSection.tsx`'s `<section ref={sectionRef}>`, an
 * ANCESTOR of this component, not this component's own root) — and React's
 * commit order runs a CHILD fiber's `useLayoutEffect` before its ANCESTOR
 * host node's ref gets attached on first mount (verified empirically: the
 * split version read `sectionRef.current` as `null` on mount, failing 2
 * tests that assert the pre-reveal `opacity:'0'` state).
 *
 * R5-P3 SUPERSEDES the above finding [ADV-18]: rather than chasing the
 * effect-timing race, the initial hidden state MOVED OUT of this file
 * entirely and into SSR'd CSS classes on the caller's JSX
 * (`SocialProofSection.tsx`'s `[data-big-stats-row]`/`[data-big-stats-curve]`
 * nodes ship `opacity-0 translate-y-3` — and, on the curve, an additional
 * `clip-path: inset(0 100% 0 0)` — baked into the FIRST byte of HTML,
 * countered by a pure-CSS `motion-reduce:` variant so a real
 * prefers-reduced-motion visitor is correct from paint one regardless of
 * JS/hydration timing). This engine NEVER writes the hidden state anymore —
 * it owns exactly two things: (1) the reveal animation once triggered, (2)
 * an unconditional VISIBLE end state on cleanup (never a bare clear-to-'' —
 * see `revealVisible` below; the CSS baseline is now "hidden", so clearing
 * an inline override would fall back to that hidden class and strand a
 * torn-down section invisible, the exact regression the old cleanup
 * contract had to avoid, achieved here by a different mechanism). This is a
 * strictly CLEANER separation of concerns than the reverted `useLayoutEffect`
 * split above ever could be — it sidesteps the ref-ownership race entirely
 * rather than working around it, so the finding above is superseded, not
 * merely deferred.
 *
 * R5-P3 ALSO adds [ADV-18]: (a) a `clip-path` sweep on the curve's own
 * reveal keyframes (folded into its existing opacity/translateY pair — one
 * WAAPI call, no new DOM node); (b) a SEPARATE, higher-bar trigger — the
 * stats ROW specifically, `>= 50%` visible + a ~150ms dwell (mirrors the
 * house dwell idiom, `HeroChatReveal.tsx`'s `BOT_SCENE_TIMING.dwellMs`) —
 * that fires `onStatsStart` ONCE so a caller can start several `NumberTicker`
 * instances together on one seam instead of each one self-observing (which
 * fired "at the band's first pixel", before the stats were comfortably in
 * view — the R4 kb "serial timers finish before attention arrives" class).
 * The block reveal (opacity/rise/clip-path) keeps the ORIGINAL, lower
 * `inViewAmount` — it may start as soon as any below-fold entrance would;
 * only the DIGITS wait for the stricter bar.
 */

import { useEffect } from 'react';
import type { RefObject } from 'react';
import { inView } from 'motion';
import {
  LANDING_MOTION_MS,
  LANDING_EASE_CSS,
  LANDING_INVIEW,
  LANDING_STAGGER_MS,
} from '../../motion/landing-motion';

/** This scene's ONE frozen timing table (Harmonization f / [AMENDED per A4]). */
export const BIG_STATS_TIMING = Object.freeze({
  /** Entry trigger geometry — same "once-and-hold" contract as every other
   * below-fold entrance on the landing. */
  inViewAmount: LANDING_INVIEW.entrance.amount,
  inViewMargin: LANDING_INVIEW.entrance.margin,
  /** Universal below-fold entrance duration + travel (12px ceiling, §1.2/§2.3). */
  riseDurationMs: LANDING_MOTION_MS.enter,
  riseTravelPx: 12,
  riseEase: LANDING_EASE_CSS.reveal,
  /** The curve block follows the stats row by one stagger step. */
  curveDelayMs: LANDING_STAGGER_MS,
  /** [ADV-18] the count-up START-SIGNAL trigger — deliberately a HIGHER,
   * separately-tuned bar than the block reveal above: the block (opacity/
   * rise/clip-path) may start as soon as any below-fold entrance would
   * (`inViewAmount` above); the DIGITS wait until the stats row is
   * substantially, dwelt-on visible, so a fast scroll-past never truncates
   * the reader's first glimpse of the tick. Numbers per r5-design-
   * amendment.md §4 / r5-checkpoint-1.md ("stats-row threshold >= 0.5 +
   * ~150ms dwell"); shape mirrors the house dwell idiom (HeroChatReveal.tsx's
   * `BOT_SCENE_TIMING.dwellMs`). */
  statsStartAmount: 0.5,
  statsStartDwellMs: 150,
});

export const BIG_STATS_ROW_SELECTOR = '[data-big-stats-row]';
export const BIG_STATS_CURVE_SELECTOR = '[data-big-stats-curve]';

interface BigStatsMotionProps {
  sectionRef: RefObject<HTMLElement | null>;
  prefersReducedMotion: boolean;
  /**
   * [ADV-18] fired ONCE, when the stats row clears `statsStartAmount`
   * visible for `statsStartDwellMs`. MUST be a stable reference
   * (`useCallback`) — it lives in this effect's dependency array, and an
   * unstable one would tear down and rebuild both observers on every parent
   * render. Optional — omit to skip the orchestration entirely (the block
   * reveal above runs unconditionally either way).
   */
  onStatsStart?: () => void;
}

/** Controls with a `.cancel()` — the native WAAPI `Animation` satisfies this. */
interface Cancelable {
  cancel: () => void;
}

export default function BigStatsMotion({
  sectionRef,
  prefersReducedMotion,
  onStatsStart,
}: BigStatsMotionProps) {
  useEffect(() => {
    // Reduced motion = STATIC_REAL: the section already renders the stats
    // row + curve at full opacity with zero transform (the `motion-reduce:`
    // CSS counter-variant on the caller's JSX — [ADV-18]) — nothing to do
    // here, including no orchestration signal (BigStat's own reduced branch
    // never mounts NumberTicker at all). A mid-session OS toggle re-selects
    // this branch on the next effect run (prefersReducedMotion is a live
    // matchMedia value — see useReducedMotion).
    if (prefersReducedMotion) return;

    const root = sectionRef.current;
    if (!root) return;

    const statsRow = root.querySelector<HTMLElement>(BIG_STATS_ROW_SELECTOR);
    const curveBlock = root.querySelector<HTMLElement>(BIG_STATS_CURVE_SELECTOR);
    if (!statsRow && !curveBlock) return;

    const targets = [statsRow, curveBlock].filter((el): el is HTMLElement => el !== null);

    let fired = false;
    let running: Cancelable[] = [];

    const commitAndRelease = (anim: Animation) => {
      running.push(anim);
      anim.finished
        .then(() => {
          // WAAPI fill:'forwards' hygiene — bake the end state, drop the handle.
          anim.commitStyles();
          anim.cancel();
        })
        .catch(() => {
          // Cancelled on unmount before finish — nothing to commit.
        });
    };

    // [ADV-18] the ONLY visible-state write this file makes now. The hidden
    // BASELINE is the caller's SSR'd CSS classes (never written here — see
    // file doc comment); this function is used both to commit the terminal
    // frame for a hidden-tab visitor and to guarantee a safe end state on
    // cleanup, in both cases moving FORWARD to visible, never back to hidden.
    const revealVisible = () => {
      targets.forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
        if (el === curveBlock) el.style.clipPath = 'inset(0 0% 0 0)';
      });
    };

    const stop = inView(
      root,
      () => {
        if (fired) return;
        fired = true;

        // Hidden tab: never animate off-screen — commit the final state.
        if (typeof document !== 'undefined' && document.hidden) {
          revealVisible();
          return;
        }

        if (statsRow) {
          commitAndRelease(
            statsRow.animate(
              [
                { opacity: 0, transform: `translateY(${BIG_STATS_TIMING.riseTravelPx}px)` },
                { opacity: 1, transform: 'none' },
              ],
              {
                duration: BIG_STATS_TIMING.riseDurationMs,
                easing: BIG_STATS_TIMING.riseEase,
                fill: 'forwards',
              }
            )
          );
        }
        if (curveBlock) {
          // [ADV-18] clip-path folded into the SAME keyframe pair as the
          // pre-existing opacity/translateY — one WAAPI call, no new DOM
          // node. Left-to-right sweep: the right inset shrinks 100% -> 0%.
          commitAndRelease(
            curveBlock.animate(
              [
                {
                  opacity: 0,
                  transform: `translateY(${BIG_STATS_TIMING.riseTravelPx}px)`,
                  clipPath: 'inset(0 100% 0 0)',
                },
                { opacity: 1, transform: 'none', clipPath: 'inset(0 0% 0 0)' },
              ],
              {
                duration: BIG_STATS_TIMING.riseDurationMs,
                delay: BIG_STATS_TIMING.curveDelayMs,
                easing: BIG_STATS_TIMING.riseEase,
                fill: 'forwards',
              }
            )
          );
        }
      },
      { amount: BIG_STATS_TIMING.inViewAmount, margin: BIG_STATS_TIMING.inViewMargin }
    );

    // [ADV-18] the SEPARATE, higher-bar count-up start-signal trigger — see
    // file doc comment. Independent of the block-reveal observer above:
    // different target (statsRow only), different threshold, own dwell.
    let statsStartFired = false;
    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const stopStatsStart = statsRow
      ? inView(
          statsRow,
          () => {
            dwellTimer = setTimeout(() => {
              if (statsStartFired) return;
              statsStartFired = true;
              onStatsStart?.();
            }, BIG_STATS_TIMING.statsStartDwellMs);
            return () => {
              if (dwellTimer) clearTimeout(dwellTimer);
              dwellTimer = null;
            };
          },
          { amount: BIG_STATS_TIMING.statsStartAmount }
        )
      : undefined;

    return () => {
      stop();
      stopStatsStart?.();
      if (dwellTimer) clearTimeout(dwellTimer);
      running.forEach((c) => {
        try {
          c.cancel();
        } catch {
          // Already finished/cancelled — nothing to undo.
        }
      });
      running = [];
      // [ADV-18] MUST write the explicit visible end-state, never clear to
      // '' — the hidden baseline is now a CSS class (see file doc comment),
      // so clearing an inline override would fall back to that hidden class
      // and strand a torn-down section invisible. This is the one behavior
      // change from the pre-R5-P3 cleanup contract (see updated test).
      revealVisible();
    };
  }, [sectionRef, prefersReducedMotion, onStatsStart]);

  return null;
}
